/** Prompt library page — personal + template prompts with CRUD (FR-PRM-01). */

import { useState } from "react";
import {
  Box, Typography, Card, CardContent, CardActions, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, Grid2 as Grid,
  CircularProgress, IconButton, InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { usePrompts, useCreatePrompt, useUpdatePrompt, useDeletePrompt } from "@/hooks/usePrompts";
import { useFeatureGate } from "@/hooks/useFeatureGate";
import type { Prompt } from "@/types";

export default function Prompts() {
  const { data, isLoading } = usePrompts();
  const { hasAccess: isAdmin } = useFeatureGate("admin");
  const createPrompt = useCreatePrompt();
  const updatePrompt = useUpdatePrompt();
  const deletePrompt = useDeletePrompt();
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState<{ open: boolean; mode: "create" | "edit"; promptType: string; prompt?: Prompt }>({ open: false, mode: "create", promptType: "personal" });
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  const filter = (prompts: Prompt[]) =>
    prompts.filter((p) => !search || p.title.toLowerCase().includes(search.toLowerCase()));

  const openCreate = (promptType: string) => {
    setTitle("");
    setContent("");
    setDialog({ open: true, mode: "create", promptType });
  };

  const openEdit = (prompt: Prompt) => {
    setTitle(prompt.title);
    setContent(prompt.content);
    setDialog({ open: true, mode: "edit", promptType: prompt.promptType, prompt });
  };

  const handleSave = () => {
    if (dialog.mode === "create") {
      createPrompt.mutate({ title, content, promptType: dialog.promptType }, { onSuccess: () => setDialog((d) => ({ ...d, open: false })) });
    } else if (dialog.prompt) {
      updatePrompt.mutate({ promptId: dialog.prompt.promptId, title, content }, { onSuccess: () => setDialog((d) => ({ ...d, open: false })) });
    }
  };

  if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;

  const personal = filter(data?.personalPrompts ?? []);
  const templates = filter(data?.templatePrompts ?? []);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Prompt Library</Typography>
      <TextField placeholder="Search prompts…" size="small" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ mb: 3, minWidth: 250 }} slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> } }} />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="h6">My Prompts</Typography>
        <Button startIcon={<AddIcon />} onClick={() => openCreate("personal")} size="small">New Prompt</Button>
      </Box>
      <PromptGrid prompts={personal} onEdit={openEdit} onDelete={(id) => deletePrompt.mutate(id)} canEdit />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, mt: 4 }}>
        <Typography variant="h6">Templates</Typography>
        {isAdmin && <Button startIcon={<AddIcon />} onClick={() => openCreate("template")} size="small">New Template</Button>}
      </Box>
      <PromptGrid prompts={templates} onEdit={openEdit} onDelete={(id) => deletePrompt.mutate(id)} canEdit={isAdmin} />

      <Dialog open={dialog.open} onClose={() => setDialog((d) => ({ ...d, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog.mode === "create" ? "New" : "Edit"} {dialog.promptType === "template" ? "Template" : "Prompt"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
          <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} size="small" fullWidth inputProps={{ maxLength: 100 }} />
          <TextField label="Content" value={content} onChange={(e) => setContent(e.target.value)} multiline rows={6} fullWidth inputProps={{ maxLength: 10000 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog((d) => ({ ...d, open: false }))}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!title || !content}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function PromptGrid({ prompts, onEdit, onDelete, canEdit }: { prompts: Prompt[]; onEdit: (p: Prompt) => void; onDelete: (id: string) => void; canEdit: boolean }) {
  if (prompts.length === 0) return <Typography color="text.secondary" sx={{ py: 2 }}>No prompts yet.</Typography>;
  return (
    <Grid container spacing={2}>
      {prompts.map((p) => (
        <Grid key={p.promptId} size={{ xs: 12, sm: 6, md: 4 }}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="subtitle2" noWrap>{p.title}</Typography>
                <Chip label={p.promptType === "template" ? "Template" : "Personal"} size="small" color={p.promptType === "template" ? "success" : "primary"} />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical" }}>
                {p.content}
              </Typography>
            </CardContent>
            {canEdit && (
              <CardActions>
                <IconButton size="small" onClick={() => onEdit(p)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => onDelete(p.promptId)}><DeleteIcon fontSize="small" /></IconButton>
              </CardActions>
            )}
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}
